// Bilingual translations + i18n context for Ma Roadmap PAM.
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const dict = {
    fr: {
        brand: {
            product: "Ma Roadmap PAM",
            tagline: "Diagnostic de maturité et feuille de route personnalisée",
            company: "Ariovis · Pure-player IAM / PAM",
        },
        nav: {
            method: "Méthode",
            faq: "Questions",
            start: "Construire ma roadmap",
            back_home: "Retour à l'accueil",
        },
        landing: {
            eyebrow: "Diagnostic PAM gratuit",
            h1_a: "Votre stratégie PAM",
            h1_b: "ne devrait pas commencer",
            h1_c: "par un bastion.",
            subtitle:
                "Évaluez votre maturité et obtenez une feuille de route personnalisée pour gouverner, contextualiser et sécuriser vos accès privilégiés.",
            cta_primary: "Construire ma roadmap PAM",
            cta_secondary: "Voir la méthode",
            reassurance: [
                "Environ 7 minutes",
                "Aucun accès technique requis",
                "Rapport PDF personnalisé",
                "Recommandations adaptées",
                "Données déclaratives uniquement",
            ],
            manifest_title:
                "Le privilège est un accès à gouverner, pas seulement un mot de passe à protéger.",
            manifest_intro:
                "En 2026, une stratégie PAM moderne ne se mesure plus au nombre de comptes placés dans un coffre. Elle se mesure à la capacité de l'organisation à supprimer, gouverner, limiter, tracer et révoquer les privilèges.",
            manifest_questions: [
                { k: "Qui peut demander ?", d: "Définir en amont qui est éligible à un privilège, sur quel périmètre et pour quelle durée." },
                { k: "Comment l'identité est-elle vérifiée ?", d: "Exiger une authentification forte et une identité de confiance avant tout accès sensible." },
                { k: "Dans quel contexte l'accès est-il autorisé ?", d: "Adapter la décision au terminal, au risque et au ticket ou changement en cours." },
                { k: "Quel privilège est réellement nécessaire ?", d: "Accorder juste ce qui est nécessaire, juste le temps d'une activité — jamais un compte permanent." },
                { k: "Comment est-il retiré et tracé ?", d: "Révoquer automatiquement, nettoyer la session et conserver des preuves exploitables." },
            ],
            chain_title: "Une chaîne de responsabilités, pas un catalogue.",
            chain_intro:
                "Ariovis conçoit une architecture cohérente qui articule cinq responsabilités complémentaires, avec un rôle transversal d'intégrateur.",
            chain: [
                { title: "Gouverner", desc: "L'éligibilité, la demande, la validation, la durée et le retrait des habilitations privilégiées." },
                { title: "Authentifier", desc: "L'assurance d'identité, le MFA, la fédération et l'entrée dans les parcours sensibles." },
                { title: "Décider", desc: "L'autorisation dynamique selon l'identité, la ressource, l'action et le contexte." },
                { title: "Contrôler", desc: "L'application du privilège temporaire, l'activité et la session — jusqu'au retrait." },
                { title: "Protéger les secrets", desc: "Le coffre-fort, le partage contrôlé et les accès distants pragmatiques." },
                { title: "Concevoir & intégrer", desc: "L'architecture, l'orchestration et l'accompagnement de bout en bout." },
            ],
            method_title: "Une trajectoire en cinq étapes.",
            method_intro:
                "Chaque diagnostic conduit à une feuille de route articulée en cinq étapes complémentaires — parce qu'une stratégie PAM se déploie, elle ne s'achète pas.",
            method_steps: [
                { name: "Identifier", desc: "Retrouver de la visibilité : recenser les comptes, les propriétaires, les prestataires, les privilèges permanents et les secrets." },
                { name: "Protéger", desc: "Rendre les comptes nominatifs, activer le MFA, mettre les mots de passe sensibles au coffre et encadrer les prestataires." },
                { name: "Gouverner", desc: "Formaliser propriétaires, demandes, validations, durées, revues et retrait automatique des accès privilégiés." },
                { name: "Dynamiser", desc: "Passer d'un compte permanent à une activité temporaire (JIT/JEA), avec des décisions qui tiennent compte du contexte." },
                { name: "Industrialiser", desc: "Étendre la couverture, contrôler en continu, détecter les dérives et intégrer les identités non humaines." },
            ],
            faq_title: "Tout savoir avant de démarrer.",
            faq_items: [
                { q: "Ce diagnostic est-il un comparateur de solutions PAM ?", a: "Non. Il permet de construire une trajectoire de sécurisation et d'identifier les capacités nécessaires. Il ne compare pas les éditeurs entre eux." },
                { q: "Recommandez-vous toujours un bastion ?", a: "Non. Une partie des besoins peut être traitée par la gouvernance, l'authentification, l'autorisation dynamique, l'automatisation ou la gestion des secrets." },
                { q: "Pourquoi une approche par capacités ?", a: "Parce qu'un privilège doit être gouverné, authentifié, décidé, appliqué puis tracé — dans cet ordre. Le choix d'une technologie ne remplace ni une stratégie, ni une gouvernance." },
                { q: "Dois-je déployer toutes les solutions ?", a: "Non. La trajectoire dépend de vos risques, de votre maturité et de vos cas d'usage. Certaines briques ne sont pas prioritaires pour tout le monde." },
                { q: "Ariovis peut-il intégrer plusieurs briques ?", a: "Oui. Ariovis conçoit l'architecture, réalise les intégrations et accompagne le déploiement — c'est notre rôle de pure-player IAM." },
                { q: "Mes données sont-elles conservées ?", a: "Les réponses sont déclaratives et servent uniquement à construire votre feuille de route et vous contacter à votre demande." },
            ],
            central_message:
                "Une stratégie PAM moderne gouverne, authentifie, décide, limite et contrôle les privilèges. Le bastion n'en est qu'une composante.",
        },
        wizard: {
            step_of: "Étape",
            of: "sur",
            back: "Précédent",
            next: "Continuer",
            finish: "Voir ma trajectoire",
            required: "Merci de répondre à cette étape.",
            step_titles: [
                "Votre contexte",
                "Vos risques",
                "Gouvernance",
                "Authentification & autorisation",
                "Privilèges & sessions",
                "Secrets & comptes techniques",
                "Recevoir votre rapport",
            ],
            step_subtitles: [
                "Quelques questions rapides pour cadrer votre organisation.",
                "Cochez les situations qui vous concernent aujourd'hui.",
                "Où en êtes-vous sur la gouvernance des accès privilégiés ?",
                "Comment sont vérifiés l'identité et le contexte avant un accès sensible ?",
                "Où en êtes-vous sur le privilège temporaire et le contrôle des sessions techniques ?",
                "Comment sont sécurisés les secrets, les comptes techniques et les accès distants ?",
                "Personnalisez votre feuille de route et recevez le rapport PDF.",
            ],
            teaser_title: "Votre trajectoire est prête.",
            teaser_desc:
                "Recevez le détail des actions, l'architecture cible et les briques recommandées par email.",
            firstname: "Prénom",
            lastname: "Nom",
            email: "Email professionnel",
            company: "Société",
            consent: "J'accepte de recevoir mon rapport par email et d'être recontacté par un expert Ariovis pour approfondir ma trajectoire.",
            submit: "Recevoir mon rapport",
            sending: "Génération en cours…",
            preview_label: "Aperçu partiel",
            axes_to_work: "Axes de travail détectés",
            current_level_short: "Niveau actuel",
            target_level_short: "Niveau cible",
            headline_risk: "Risque prioritaire",
            error_generic:
                "Une erreur est survenue. Vérifiez vos réponses et réessayez.",
        },
        results: {
            eyebrow: "Votre feuille de route PAM",
            current: "Maturité actuelle",
            target: "Maturité cible",
            confidence: "Confiance",
            synthesis: "Synthèse",
            radar: "Diagnostic par dimension",
            top_risks: "Top des risques",
            top_priorities: "Priorités de trajectoire",
            roadmap: "Feuille de route en 5 étapes",
            architecture: "Architecture cible",
            solutions: "Briques recommandées",
            non_priority: "Briques non prioritaires à ce stade",
            quick_wins: "Quick wins",
            offers: "Comment Ariovis peut vous accompagner",
            cta_pdf: "Télécharger le rapport PDF",
            cta_expert: "Planifier un atelier avec un expert Ariovis",
            impact: "Impact",
            effort: "Effort",
            solution_col: "Solution",
            action_col: "Action",
            role: "Rôle",
            capabilities: "Capacités",
            offer_names: [
                "Atelier de qualification",
                "Feuille de route PAM",
                "Socle rapide",
                "Programme intégré",
            ],
            offer_descs: [
                "Revue des risques, clarification des cas d'usage, identification des briques pertinentes.",
                "Diagnostic détaillé, matrice de risques, architecture cible, roadmap et modèle opérationnel.",
                "Configuration d'une première brique, mise en production, transfert de compétences.",
                "Gouvernance, AM, autorisation, PAM et secrets orchestrés jusqu'à l'industrialisation.",
            ],
            disclaimer:
                "Cette étude repose sur les réponses déclaratives fournies. Les capacités, la couverture réelle et l'architecture doivent être confirmées lors d'un cadrage détaillé.",
            step_prefix: "Étape",
            actions: "Actions clés",
            supports: "Briques mobilisées",
        },
        footer: {
            legal: "© " + new Date().getFullYear() + " Ariovis — Pure-player IAM / PAM européen.",
            manifest_link: "Manifeste",
            brand_link: "Charte",
            contact: "Contact",
        },
    },
    en: {
        brand: {
            product: "Ma Roadmap PAM",
            tagline: "Maturity diagnostic & personalized roadmap",
            company: "Ariovis · IAM / PAM pure-player",
        },
        nav: {
            method: "Method",
            faq: "FAQ",
            start: "Build my roadmap",
            back_home: "Back to home",
        },
        landing: {
            eyebrow: "Free PAM diagnostic",
            h1_a: "Your PAM strategy",
            h1_b: "should not start",
            h1_c: "with a bastion.",
            subtitle:
                "Assess your maturity and get a personalized roadmap to govern, contextualize and secure your privileged access.",
            cta_primary: "Build my PAM roadmap",
            cta_secondary: "See the method",
            reassurance: [
                "~7 minutes",
                "No technical access needed",
                "Personalized PDF report",
                "Tailored recommendations",
                "Declarative data only",
            ],
            manifest_title:
                "Privilege is an access to govern, not just a password to protect.",
            manifest_intro:
                "In 2026, a modern PAM strategy is no longer measured by the number of accounts placed in a vault. It is measured by the ability to remove, govern, limit, trace and revoke privileges.",
            manifest_questions: [
                { k: "Who can request?", d: "Define upfront who is eligible for a privilege, on which scope and for how long." },
                { k: "How is identity verified?", d: "Require strong authentication and a trusted identity before any sensitive access." },
                { k: "In which context is access authorized?", d: "Adapt the decision to the device, the risk and the ticket or change at hand." },
                { k: "What privilege is truly required?", d: "Grant just what is needed, just for an activity — never a permanent account." },
                { k: "How is it removed and traced?", d: "Revoke automatically, clean up the session and keep actionable evidence." },
            ],
            chain_title: "A chain of responsibilities, not a catalog.",
            chain_intro:
                "Ariovis designs a coherent architecture around five responsibilities and one cross-cutting integrator role.",
            chain: [
                { title: "Govern", desc: "Eligibility, request, approval, duration and removal of privileged entitlements." },
                { title: "Authenticate", desc: "Identity assurance, MFA, federation and safe entry into sensitive journeys." },
                { title: "Decide", desc: "Dynamic authorization based on identity, resource, action and context." },
                { title: "Control", desc: "Enforce temporary privilege, activity and session — through to removal." },
                { title: "Protect secrets", desc: "Vault, controlled sharing and pragmatic remote access." },
                { title: "Design & integrate", desc: "Architecture, orchestration and end-to-end support." },
            ],
            method_title: "A five-stage trajectory.",
            method_intro:
                "Every diagnostic leads to a five-stage roadmap — because a PAM strategy is deployed, not purchased.",
            method_steps: [
                { name: "Identify", desc: "Regain visibility: inventory, owners, standing privileges, third-parties, secrets." },
                { name: "Protect", desc: "Named accounts, MFA, vault, rotation, break-glass hardening, third-party control." },
                { name: "Govern", desc: "Owners, requests, approvals, durations, reviews, SoD, automatic removal." },
                { name: "Dynamize", desc: "JIT, JEA, administrative activities, contextual decisions, fine-grained authorization." },
                { name: "Industrialize", desc: "Extension, continuous control, detection, non-human identities, centralized policies." },
            ],
            faq_title: "Everything you need to know before starting.",
            faq_items: [
                { q: "Is this diagnostic a PAM solution comparator?", a: "No. It helps build a securing trajectory and identify the required capabilities. It does not compare vendors." },
                { q: "Do you always recommend a bastion?", a: "No. Part of the needs can be handled by governance, authentication, dynamic authorization, automation or secrets management." },
                { q: "Why a capability-first approach?", a: "Because a privilege must be governed, authenticated, decided, enforced and traced — in that order. Technology alone does not replace strategy and governance." },
                { q: "Should I deploy all the solutions?", a: "No. The trajectory depends on your risks, maturity and use cases. Some blocks are not a priority for everyone." },
                { q: "Can Ariovis integrate several blocks?", a: "Yes. Ariovis designs the architecture, delivers integrations and supports the deployment — that is our role as a pure-player." },
                { q: "Are my answers kept?", a: "Answers are declarative and used only to build your roadmap and contact you upon request." },
            ],
            central_message:
                "A modern PAM strategy governs, authenticates, decides, limits and controls privileges. The bastion is only one component of the system.",
        },
        wizard: {
            step_of: "Step",
            of: "of",
            back: "Back",
            next: "Continue",
            finish: "See my trajectory",
            required: "Please answer this step.",
            step_titles: [
                "Your context",
                "Your risks",
                "Governance",
                "Authentication & authorization",
                "Privileges & sessions",
                "Secrets & technical accounts",
                "Receive your report",
            ],
            step_subtitles: [
                "A few questions to frame your organization.",
                "Select the situations that apply to you today.",
                "Where are you on eligibility and reviewing privileged entitlements?",
                "How are identity and context verified before sensitive access?",
                "What is the level of temporary privilege enforcement and session control?",
                "How are secrets, technical accounts and remote access secured?",
                "Personalize your roadmap and receive the PDF report.",
            ],
            teaser_title: "Your trajectory is ready.",
            teaser_desc:
                "Receive the detail of actions, target architecture and recommended blocks by email.",
            firstname: "First name",
            lastname: "Last name",
            email: "Business email",
            company: "Company",
            consent: "I agree to receive my report by email and to be contacted by an Ariovis expert about my trajectory.",
            submit: "Receive my report",
            sending: "Generating…",
            preview_label: "Partial preview",
            axes_to_work: "Detected work axes",
            current_level_short: "Current level",
            target_level_short: "Target level",
            headline_risk: "Top risk",
            error_generic:
                "Something went wrong. Please check your answers and try again.",
        },
        results: {
            eyebrow: "Your PAM roadmap",
            current: "Current maturity",
            target: "Target maturity",
            confidence: "Confidence",
            synthesis: "Synthesis",
            radar: "Per-dimension diagnostic",
            top_risks: "Top risks",
            top_priorities: "Trajectory priorities",
            roadmap: "Five-stage roadmap",
            architecture: "Target architecture",
            solutions: "Recommended blocks",
            non_priority: "Blocks not prioritized at this stage",
            quick_wins: "Quick wins",
            offers: "How Ariovis can support you",
            cta_pdf: "Download the PDF report",
            cta_expert: "Book a workshop with an Ariovis expert",
            impact: "Impact",
            effort: "Effort",
            solution_col: "Solution",
            action_col: "Action",
            role: "Role",
            capabilities: "Capabilities",
            offer_names: [
                "Qualification workshop",
                "PAM roadmap",
                "Fast-start foundation",
                "Integrated programme",
            ],
            offer_descs: [
                "Risk review, use-case clarification, identification of relevant blocks.",
                "Detailed diagnostic, risk matrix, target architecture, roadmap and operating model.",
                "First-brick configuration, production rollout, knowledge transfer.",
                "Governance, AM, authorization, PAM and secrets orchestrated up to industrialization.",
            ],
            disclaimer:
                "This study is based on the declarative answers provided. Actual capabilities, coverage and architecture must be confirmed in a detailed framing.",
            step_prefix: "Stage",
            actions: "Key actions",
            supports: "Building blocks",
        },
        footer: {
            legal: "© " + new Date().getFullYear() + " Ariovis — European IAM / PAM pure-player.",
            manifest_link: "Manifesto",
            brand_link: "Brand",
            contact: "Contact",
        },
    },
};

const I18nContext = createContext({ lang: "fr", t: (k) => k, setLang: () => {} });

const LS_KEY = "ariovis-pam-lang";

function detectInitialLang() {
    try {
        const stored = localStorage.getItem(LS_KEY);
        if (stored === "fr" || stored === "en") return stored;
    } catch (e) {
        // ignore
    }
    const nav = (typeof navigator !== "undefined" && navigator.language) || "fr";
    return nav.toLowerCase().startsWith("fr") ? "fr" : "en";
}

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(detectInitialLang());

    useEffect(() => {
        try {
            localStorage.setItem(LS_KEY, lang);
        } catch (e) {
            /* ignore */
        }
        if (typeof document !== "undefined") {
            document.documentElement.setAttribute("lang", lang);
        }
    }, [lang]);

    const value = useMemo(() => {
        const t = (path) => {
            const parts = path.split(".");
            let cur = dict[lang];
            for (const p of parts) {
                if (cur == null) return path;
                cur = cur[p];
            }
            return cur == null ? path : cur;
        };
        return { lang, setLang, t };
    }, [lang]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}
